import React, { useRef, useEffect } from 'react';
import { Platform, View, StyleSheet, ViewStyle } from 'react-native';

// Interface for individual word objects
interface Word {
  text: string;
  size: number;
  color?: string;
}

// Interface for the component props
export interface WordCloudProps {
  words?: Word[];
  width?: number;
  height?: number;
  colors?: string[] | ((word: Word) => string);
  backgroundColor?: string;
  padding?: number;
  fontFamily?: string;
  rotate?: () => number;
  onWordClick?: (word: string) => void;
  style?: ViewStyle | React.CSSProperties;
}

// Default color generator function
const defaultColors = (): ((word: Word) => string) => {
  return () => `hsl(${Math.random() * 360}, 100%, 50%)`;
};

// The values below are interpolated into an HTML document that, before this
// change, ran in this page's own origin. Every tenant's app is served from one
// share host, so an app-definition attribute that closed a tag could run JS
// against other tenants' apps. Each value is now coerced to the one shape its
// surrounding context can safely accept.

/** Numeric interpolation: a non-finite value falls back to the caller's default. */
const safeNumber = (value: unknown, fallback: number): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * JS-literal interpolation. JSON.stringify alone is not enough inside a
 * `<script>` block: a string containing `</script>` would still close the tag.
 */
const jsLiteral = (value: unknown): string =>
  JSON.stringify(value ?? null)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');

/**
 * CSS-value interpolation. JSON escaping does not apply inside `<style>`, so an
 * allowlist is used instead: hex, rgb()/rgba(), hsl()/hsla(), or a CSS keyword.
 */
const CSS_COLOR = /^(#[0-9a-f]{3,8}|(rgb|hsl)a?\([0-9a-z.,%\s/+-]*\)|[a-z]+)$/i;
const safeCssColor = (value: unknown, fallback: string): string => {
  const candidate = typeof value === 'string' ? value.trim() : '';
  return CSS_COLOR.test(candidate) ? candidate : fallback;
};

// Create HTML content for both platforms
const createHtmlContent = (
  words: Word[],
  width: number,
  height: number,
  colors: string[] | ((word: Word) => string),
  backgroundColor: string,
  padding: number,
  fontFamily: string,
  rotate: () => number,
  onWordClick?: (word: string) => void
) => {
  // Prepare the words data
  const processedWords = words.map((word) => ({
    text: word.text,
    size: word.size,
    ...(word.color && { color: word.color }),
  }));

  return `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <script src="https://d3js.org/d3.v5.min.js"></script>
    <script src="https://cdnjs.cloudflare.com/ajax/libs/d3-cloud/1.2.5/d3.layout.cloud.min.js"></script>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100..900&display=swap" rel="stylesheet">
    <style>
      body { 
        margin: 0; 
        background-color: ${safeCssColor(backgroundColor, 'white')};
        overflow: hidden;
      }
      #wordcloud {
        width: ${safeNumber(width, 300)}px;
        height: ${safeNumber(height, 300)}px;
      }
    </style>
  </head>
  <body>
    <div id="wordcloud"></div>
    <script>
      const words = ${jsLiteral(processedWords)};
      const width = ${safeNumber(width, 300)};
      const height = ${safeNumber(height, 300)};
      const padding = ${safeNumber(padding, 5)};
      const fontFamily = ${jsLiteral(String(fontFamily))};
      const rotate = ${safeNumber(rotate(), 0)};
      const colors = ${
        typeof colors === 'function'
          ? colors.toString()
          : jsLiteral(colors)
      };
      // Function to check if text contains Japanese characters
      function containsJapanese(text) {
        return /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf\u31f0-\u31ff\u3220-\u3243\u3280-\u337f]/.test(text);
      }

      // Adjust the cloud layout to handle Japanese text better
      d3.layout.cloud()
        .size([width, height])
        .words(words)
        .padding(padding)
        .rotate(function(d) {
          // Optional: prevent rotation for Japanese text for better readability
          return rotate > 0 ? ~~(Math.random() * 2) * 90 : 0;
        })
        .font(function(d) {
          // Always use Noto Sans JP for Japanese text, fallback to specified font for others
          return containsJapanese(d.text) ? 'Noto Sans JP' : fontFamily;
        })
        .fontSize(function(d) {
          // Apply different scaling for Japanese vs non-Japanese text
          const baseSize = d.size * 5; // Base scaling factor
          return containsJapanese(d.text) ? baseSize * 1.5 : baseSize;
        })
        .on('end', draw)
        .start();

      function draw(words) {
        const svg = d3.select('#wordcloud').append('svg')
          .attr('width', width)
          .attr('height', height)
          .append('g')
          .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');

        svg.selectAll('text')
          .data(words)
          .enter().append('text')
          .style('font-size', d => d.size + 'px')
          .style('font-family', d => containsJapanese(d.text) ? 'Noto Sans JP, sans-serif' : fontFamily)
          .style('fill', d => {
            if (d.color) return d.color;
            return typeof colors === 'function' ? colors(d) : colors[Math.floor(Math.random() * colors.length)];
          })
          .attr('text-anchor', 'middle')
          .attr('transform', d => 'translate(' + [d.x, d.y] + ')rotate(' + d.rotate + ')')
          .text(d => d.text)
          ${
            onWordClick
              ? `.style('cursor', 'pointer')
                .on('click', function(d) {
                  window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'wordClick', word: d.text }));
                })`
              : ''
          };
      }
    </script>
  </body>
  </html>
  `;
};

// Split into two separate components to avoid any cross-loading
// Web Component
const WebWordClouds: React.FC<WordCloudProps> = (props) => {
  const {
    words = [],
    width = 300,
    height = 300,
    colors = defaultColors(),
    backgroundColor = 'white',
    padding = 5,
    fontFamily = 'Impact',
    rotate = () => ~~(Math.random() * 2) * 90,
    onWordClick,
    style,
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const htmlContent = createHtmlContent(
    words,
    width,
    height,
    colors,
    backgroundColor,
    padding,
    fontFamily,
    rotate,
    onWordClick
  );

  useEffect(() => {
    if (iframeRef.current) {
      // Add message event listener for iframe communication
      const handleIframeMessage = (event: MessageEvent) => {
        // A sandboxed frame posts from an opaque origin, which no event.origin
        // comparison can match, so the sender is identified by window reference.
        // Without this, any page could post a wordClick into the action chain.
        if (event.source !== iframeRef.current?.contentWindow) return;
        if (event.data && event.data.type === 'wordClick' && onWordClick) {
          onWordClick(event.data.word);
        }
      };

      window.addEventListener('message', handleIframeMessage);

      return () => {
        window.removeEventListener('message', handleIframeMessage);
      };
    }
  }, [onWordClick]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={htmlContent}
      // Deliberately without allow-same-origin: the document needs nothing from
      // this origin, and withholding it gives the frame an opaque origin, so a
      // string that still escaped the interpolation above cannot read the share
      // host's storage or act as the signed-in user.
      sandbox="allow-scripts"
      style={{
        width: width,
        height: height,
        border: 'none',
        backgroundColor,
        ...(style as React.CSSProperties),
      }}
      title="Word Cloud"
    />
  );
};

const WebWordCloud: React.FC<WordCloudProps> = (props) => {
  if (Platform.OS === 'web') {
    return <WebWordClouds {...props} />;
  } else {
    return <></>;
  }
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default WebWordCloud;
